import React, { useEffect, useState } from 'react';
import { Sparkles, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';
import { AAWTA } from '@/lib/aawta';

const normalizeUrl = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};
const openExternal = (url) => url && window.open(url, '_blank', 'noopener,noreferrer');

const SponsorCard = ({ s }) => {
  const [expanded, setExpanded] = useState(false);
  const website = normalizeUrl(s.cta_url) || normalizeUrl(s.website);
  const desc = s.description || '';
  const story = s.company_story || '';
  const combined = [desc, story].filter(Boolean).join('\n\n');
  const isLong = combined.length > 220;
  const short = combined.length > 220 ? combined.slice(0, 220).trimEnd() + '…' : combined;

  return (
    <div data-testid={`sponsor-card-${s.id}`} className="rounded-2xl bg-white border border-aawta-border p-6 shadow-aawta-soft hover:shadow-aawta-elevated hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {s.banner_file_id ? (
        <img src={fileUrl(s.banner_file_id)} alt={s.name} className="w-full h-40 object-cover rounded-xl bg-aawta-surface" />
      ) : s.logo_file_id ? (
        <div className="w-full h-40 rounded-xl bg-white border border-aawta-border flex items-center justify-center">
          <img src={fileUrl(s.logo_file_id)} alt={s.name} className="max-h-24 max-w-[70%] object-contain" />
        </div>
      ) : (
        <div className="w-full h-40 rounded-xl bg-aawta-surface flex items-center justify-center">
          <Sparkles className="h-8 w-8 text-aawta-magenta" />
        </div>
      )}
      {s.level && <div className="mt-4 inline-flex self-start items-center px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[10.5px] font-bold uppercase tracking-wider">{s.level}</div>}
      <h3 className="mt-3 font-display font-semibold text-xl text-aawta-navy">{s.name}</h3>

      {combined && (
        <div className="mt-2">
          <p className={`text-[14.5px] text-aawta-muted leading-relaxed whitespace-pre-wrap`} data-testid={`sponsor-description-${s.id}`}>
            {expanded ? combined : short}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`sponsor-readmore-${s.id}`}
              className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
            >
              {expanded ? (<>Read Less <ChevronUp className="h-3.5 w-3.5" /></>) : (<>Read More <ChevronDown className="h-3.5 w-3.5" /></>)}
            </button>
          )}
        </div>
      )}

      {website && (
        <Button
          onClick={() => openExternal(website)}
          variant="outline"
          data-testid={`sponsor-website-${s.id}`}
          className="mt-4 rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium self-start"
        >
          {s.cta_label || 'Visit Website'}
          <ExternalLink className="ml-2 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};

export const SponsorShowcase = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get('/sponsors').then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  const hasSponsors = items && items.length > 0;

  return (
    <section id="sponsor-showcase" className="py-24 md:py-32 bg-aawta-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Sponsor Showcase</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              {hasSponsors ? 'Meet our sponsors.' : 'Sponsor Showcase.'}
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Meet the organizations investing in workforce development, education, innovation,
              and economic opportunity through the Future Workforce Summit.
            </p>
          </div>
          <Button
            onClick={() => openExternal(AAWTA.zeffySponsorUrl)}
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all"
            data-testid="sponsor-showcase-become-cta"
          >
            Become a Sponsor
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {hasSponsors ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((s) => <SponsorCard key={s.id} s={s} />)}
          </div>
        ) : (
          <div className="mt-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[11px] font-bold uppercase tracking-[0.18em] mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Coming Soon
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-white border border-dashed border-aawta-border flex flex-col items-center justify-center gap-2">
                  <Sparkles className="h-5 w-5 text-aawta-navy/30" />
                  <div className="text-[10px] tracking-[0.2em] uppercase text-aawta-navy/50 font-semibold">Coming Soon</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SponsorShowcase;
